/**
 * Zámek rozměrů Výkopu po přechodu na Vykopaný (PHASE.1).
 *
 * Nový ceník (`volume`, m³/m²): PHASE.1 už účtuje zeminu + povrch, Obnova (PHASE.2)
 * bere objem z `excavationSurfaces`. Metry se ve Vykopaném zamykají.
 * Starý ceník (`linear`, délkové metry, kompilovaný výchozí): metry zůstávají
 * editovatelné i ve Vykopaném. Od PHASE.2 je zámek pro oba ceníky.
 *
 * Obnova (obnovaSurfaces, hrabičkování / ROZVOZ) zůstává editovatelná dle fáze.
 */

import { REMOVAL_BEYOND_SCOPE } from '../constants/excavation-material.constants';
import {
  isObnovaSheetEditablePhase,
  isVykopSheetEditablePhase,
} from './excavation-surfaces-phase.util';
import {
  inferExcavationPricingModeFromSurfaces,
  readExplicitExcavationPricingMode,
  type ExcavationPricingModeEvidence,
} from './excavation-pricing-mode.util';

/** Metadata klíče snapshotu 1. fáze (Výkop) — parita BE `VYKOP_PHASE_SNAPSHOT_METADATA_KEYS`. */
export const VYKOP_DIMENSION_LOCK_SNAPSHOT_KEYS = [
  'excavationSurfaces',
  'excavationLengthM',
  'excavationMaterial',
  'excavationMaterialSubtype',
  'excavationDeepeningCm',
  'removalBeyondScope',
  'removalBeyondScopeQuantity',
  'removalBeyondScopeWidthM',
  'removalBeyondScopeDepthM',
  'removalBeyondScopeLengthM',
] as const;

export type VykopDimensionLockSnapshotKey = (typeof VYKOP_DIMENSION_LOCK_SNAPSHOT_KEYS)[number];

export type ExcavationDataLike = Record<string, unknown> & {
  excavationSurfaces?: unknown;
  specialRemovalRows?: unknown;
  excavationPricingMode?: unknown;
  obnovaSurfaces?: unknown;
};

export type VykopDimensionLockInput = {
  objectType: string | null | undefined;
  status: string | null | undefined;
  /** True = existují primární řádky výkazu pro PHASE.1 (Výkop). */
  hasPhase1ExcavationLedger?: boolean;
  /**
   * `linear` = starý ceník (délkové metry). `volume` = nový ceník (m³/m²).
   * Chybí-li explicitní režim, `surfaceBucket` / `obnovaSurfaces` znamenají nový ceník;
   * jinak jde o kompilovaný starý ceník.
   */
  excavationPricingMode?: unknown;
  excavationSurfaces?: unknown;
  obnovaSurfaces?: unknown;
};

/** Nový ceník (m³/m²). Explicitní `linear` má přednost před buckety. */
function isNewVolumePriceList(input: VykopDimensionLockInput): boolean {
  const explicit = readExplicitExcavationPricingMode(input.excavationPricingMode);
  if (explicit === 'volume') return true;
  if (explicit === 'linear') return false;
  return (
    inferExcavationPricingModeFromSurfaces({
      excavationSurfaces: input.excavationSurfaces as ExcavationPricingModeEvidence['excavationSurfaces'],
      obnovaSurfaces: input.obnovaSurfaces as ExcavationPricingModeEvidence['obnovaSurfaces'],
    }) === 'volume'
  );
}

/**
 * Rozměry Výkopu: PHASE.0 vždy (dokud není PHASE.1 ledger).
 * Ve Vykopaném (PHASE.1) jen starý ceník (`linear`). Nový ceník (`volume`) je zamčený.
 * Od PHASE.2 zamčeno pro oba.
 */
export function isVykopDimensionEditAllowed(input: VykopDimensionLockInput): boolean {
  if (input.hasPhase1ExcavationLedger === true) return false;
  if (isVykopSheetEditablePhase(input.objectType, input.status)) return true;
  if (!isObnovaSheetEditablePhase(input.objectType, input.status)) return false;
  return !isNewVolumePriceList(input);
}

/** True = UI má zamknout pole Výkopu (náhled), Obnova může zůstat editovatelná. */
export function areVykopDimensionsLocked(input: VykopDimensionLockInput): boolean {
  return !isVykopDimensionEditAllowed(input);
}

function stableJson(value: unknown): string {
  return JSON.stringify(value ?? null);
}

function isVykopSpecialRemovalCode(code: string): boolean {
  return code === REMOVAL_BEYOND_SCOPE.BOURANI_BETON_ZAKLADY;
}

function isObnovaSpecialRemovalCode(code: string): boolean {
  return code === REMOVAL_BEYOND_SCOPE.ROZVOZ_ZEMINA;
}

function asRowList(raw: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object' && !Array.isArray(r))
    .map((r) => ({ ...r }));
}

/**
 * Sloučí specialRemovalRows: při zámku zachová bourání z existujícího snapshotu,
 * vezme Obnova řádky (rozvoz) z incoming.
 */
export function mergeSpecialRemovalRowsPreservingLockedVykop(
  existingRows: unknown,
  incomingRows: unknown,
  locked: boolean,
): unknown {
  if (!locked) return incomingRows;
  const existing = asRowList(existingRows);
  const incoming = asRowList(incomingRows);
  const keptVykop = existing.filter((r) => isVykopSpecialRemovalCode(String(r['code'] ?? '').trim()));
  const incomingObnova = incoming.filter((r) =>
    isObnovaSpecialRemovalCode(String(r['code'] ?? '').trim()),
  );
  const otherIncoming = incoming.filter((r) => {
    const code = String(r['code'] ?? '').trim();
    return !isVykopSpecialRemovalCode(code) && !isObnovaSpecialRemovalCode(code);
  });
  const merged = [...keptVykop, ...incomingObnova, ...otherIncoming];
  return merged.length > 0 ? merged : incomingRows === undefined ? undefined : [];
}

export type SanitizeLockedVykopResult = {
  data: ExcavationDataLike;
  /** True = klient se pokusil změnit zamčený Výkop snapshot. */
  strippedVykopMutation: boolean;
  changedKeys: string[];
};

/**
 * Po PHASE.1 / při ledgeru: zachová Výkop snapshot z existujících metadat,
 * nechá projít Obnova pole (`obnovaSurfaces`, rozvoz, …).
 */
export function sanitizeExcavationDataPreservingLockedVykop(
  existingMeta: Record<string, unknown> | null | undefined,
  incoming: ExcavationDataLike | null | undefined,
  locked: boolean,
): SanitizeLockedVykopResult {
  if (!incoming || typeof incoming !== 'object') {
    return { data: {}, strippedVykopMutation: false, changedKeys: [] };
  }
  if (!locked) {
    return { data: { ...incoming }, strippedVykopMutation: false, changedKeys: [] };
  }

  const meta = existingMeta ?? {};
  const next: ExcavationDataLike = { ...incoming };
  const changedKeys: string[] = [];

  for (const key of VYKOP_DIMENSION_LOCK_SNAPSHOT_KEYS) {
    const existingVal = meta[key];
    const incomingVal = incoming[key];
    if (incomingVal === undefined) continue;
    if (stableJson(incomingVal) === stableJson(existingVal)) continue;
    changedKeys.push(key);
    if (existingVal === undefined) {
      delete next[key];
    } else {
      next[key] = existingVal as ExcavationDataLike[typeof key];
    }
  }

  if (incoming.specialRemovalRows !== undefined) {
    const merged = mergeSpecialRemovalRowsPreservingLockedVykop(
      meta['specialRemovalRows'],
      incoming.specialRemovalRows,
      true,
    );
    if (stableJson(merged) !== stableJson(incoming.specialRemovalRows)) {
      changedKeys.push('specialRemovalRows');
    }
    next.specialRemovalRows = merged;
  }

  // Režim ceníku z Výkopu — nepřepisovat po zámku (pojistka vedle pricing-mode util).
  if (
    incoming.excavationPricingMode !== undefined &&
    meta['excavationPricingMode'] != null &&
    stableJson(incoming.excavationPricingMode) !== stableJson(meta['excavationPricingMode'])
  ) {
    changedKeys.push('excavationPricingMode');
    next.excavationPricingMode = meta['excavationPricingMode'];
  }

  return {
    data: next,
    strippedVykopMutation: changedKeys.length > 0,
    changedKeys,
  };
}
